import { useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from '../app/hooks';
import { clearSession } from '../app/store/clearSession';
import { changeUser } from '../app/store/userStore';
import { useGetAuthUserQuery } from '../services/authApi';

/**
 * Bootstraps the authenticated session: loads the current user, syncs it into
 * the store, redirects away from /login once authenticated, and clears the
 * session if the user request fails.
 */
export function useAuthSession() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const { token } = useAppSelector((state) => state.auth);

  const {
    data: responseDataUser,
    isError,
    isLoading,
    isSuccess,
  } = useGetAuthUserQuery(null, { skip: !token });

  const isTokenValid = Boolean(token && isSuccess && responseDataUser);

  // The URL the user first landed on, so we can restore it after login.
  const firstUrl = useMemo(() => location.pathname + location.search, []);

  useEffect(() => {
    if (isSuccess && responseDataUser) {
      dispatch(changeUser(responseDataUser));

      if (location.pathname + location.search === '/login') {
        navigate(firstUrl !== '/login' ? firstUrl : '/', { replace: true });
      }
    }
  }, [
    isSuccess,
    responseDataUser,
    dispatch,
    navigate,
    location.pathname,
    location.search,
    firstUrl,
  ]);

  useEffect(() => {
    if (isError) {
      dispatch(clearSession());
    }
  }, [isError, dispatch]);

  return { isTokenValid, isLoadingUser: isLoading };
}
